import Table from "../Table/Table";
import ContactBlock from "../ContentBlock/ContactBlock";
import axios from "axios";
import SearchField from "./SearchField";
import FilterPanel from "../Filters/FilterPanel";
import { useFilterParams } from "../hooks/useFilterParams";
import React, { useEffect, useState, useCallback } from "react";
import { useGlobalState } from "../GlobalContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft, faArrowRight } from "@fortawesome/free-solid-svg-icons";
import debounce from "lodash/debounce";
import { apiLabelUrl } from "../Functions/functions";

// Status options for orders
const ORDER_STATUS_OPTIONS = [
    { value: "order", label: "Order" },
    { value: "productie", label: "Production" },
    { value: "geleverd", label: "Delivered" },
    { value: "afgewezen", label: "Rejected" },
    { value: "verzonden", label: "Sent" },
];

export default function OrderOverview(props) {
    const { textData, responsive } = props;
    const { loggedInUser } = useGlobalState();
    let [orderData, setOrderData] = useState([]);
    let [allOrders, setAllOrders] = useState([]);
    const [openPopup, setOpenPopup] = useState(null);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [groupedData, setGroupedData] = useState(null);

    // Initialize filters from URL
    const { filters, updateFilter, resetFilters, getApiParams } = useFilterParams({
        status: null,
        date_from: null,
        date_to: null,
        price_min: null,
        price_max: null,
        sort_by: null,
        sort_direction: null,
        group_by: null,
        search: null,
    });

    // Fetch orders when page or filters change
    useEffect(() => {
        debouncedGetOrders(currentPage);
    }, [currentPage, filters]);

    useEffect(() => {
        loadSelectedOrder();
    }, [orderData]);

    const loadSelectedOrder = () => {
        if (openPopup !== null) return;
        if (typeof orderData[0]?.orders !== "undefined" && orderData[0]?.orders.length !== 0) {
            // get id from url param
            let urlParams = new URLSearchParams(window.location.search);
            let id = urlParams.get("id");

            if (id !== null) {
                for (let orderId in orderData[0]?.orders) {
                    if (orderData[0]?.orders[orderId].project_number == id) {
                        setOpenPopup({ id: orderData[0]?.orders[orderId].project_number, type: "order" });
                        break;
                    }
                }
            }
        }
    };

    const getOrders = async (page) => {
        setLoading(true);
        const ordersAPI = (await apiLabelUrl("api")) + "/orders";

        // Build params from filters
        const filterParams = getApiParams();

        axios
            .get(ordersAPI, {
                params: {
                    user_id: loggedInUser.user.id,
                    page: page,
                    per_page: 5,
                    ...filterParams,
                },
            })
            .then((response) => {
                // Check if response is grouped
                if (response.data.grouped) {
                    setGroupedData(response.data);
                    setOrderData([]);
                    setAllOrders([]);
                    setTotalPages(1);
                } else {
                    setGroupedData(null);
                    let result = response.data;
                    result.section = "orders";
                    setOrderData([result]);
                    setAllOrders([result]);
                    setTotalPages(response.data.total_pages);
                }
                setLoading(false);
            })
            .catch((error) => {
                console.error(error);
                setLoading(false);
                setGroupedData(null);
                setOrderData([]);
            });
    };

    // Create a debounced version of the getOrders function
    const debouncedGetOrders = useCallback(
        debounce((page) => getOrders(page), 500),
        [filters]
    );

    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= totalPages) {
            setCurrentPage(newPage);
        }
    };

    // Handle filter apply (for mobile)
    const handleFilterApply = () => {
        setCurrentPage(1);
        getOrders(1);
    };

    // Get translated status options
    const getStatusOptions = () => {
        return ORDER_STATUS_OPTIONS.map((opt) => ({
            value: opt.value,
            label: textData?.["filters"]?.[`status_${opt.value}`] || opt.label,
        }));
    };

    return (
        <>
            {/* Filter Panel */}
            <div className="orders-filter-section">
                <FilterPanel
                    filters={filters}
                    updateFilter={updateFilter}
                    resetFilters={resetFilters}
                    onApply={handleFilterApply}
                    textData={textData}
                    statusOptions={getStatusOptions()}
                    responsive={responsive}
                />
            </div>

            {/* Grouped Data Display */}
            {groupedData && groupedData.groups && groupedData.groups.length > 0 && (
                <>
                    <div className="orders-overview-title">
                        <h2>
                            {textData?.["account/order_overview"]?.["table"]?.["title"] || "Orders"}({groupedData.total}
                            )
                        </h2>
                    </div>
                    <Table
                        responsive={responsive}
                        groupedData={groupedData}
                        textData={!!textData && textData["account/order_overview"]}
                        textDataLabel={!!textData && textData["sections/label"]}
                        navigateToPageName={props.navigateToPageName}
                        allTextData={!!textData && textData}
                        openPopup={openPopup}
                        loading={loading}
                    />
                </>
            )}

            {/* Regular Data Display */}
            {!groupedData && typeof orderData[0]?.orders !== "undefined" && orderData[0]?.orders.length !== 0 && (
                <>
                    <Table
                        responsive={responsive}
                        data={orderData}
                        textData={!!textData && textData["account/order_overview"]}
                        textDataLabel={!!textData && textData["sections/label"]}
                        navigateToPageName={props.navigateToPageName}
                        allTextData={!!textData && textData}
                        openPopup={openPopup}
                        loading={loading}
                    />
                    <div className="pagination-controls">
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>
                        <span>
                            &nbsp;Page {currentPage} of {totalPages}&nbsp;
                        </span>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
                            <FontAwesomeIcon icon={faArrowRight} />
                        </button>
                    </div>
                </>
            )}

            {/* No Data Message */}
            {!groupedData &&
                (typeof orderData[0]?.orders === "undefined" || orderData[0]?.orders.length === 0) &&
                !loading && <h2>{!!textData && textData["account/order_overview"]["table"]["no_data"]}</h2>}

            {/* Loading State */}
            {loading && orderData.length === 0 && !groupedData && (
                <div className="modelviewer-loading centered">
                    <span className="spinner"></span>
                </div>
            )}

            {/* Contact Block for Mobile */}
            {!!responsive && !!textData && !!textData["content_blocks/contact"] && (
                <ContactBlock responsive={responsive} textData={textData["content_blocks/contact"]} />
            )}
        </>
    );
}
